import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import InputNumber from '../../../components/InputNumber/InputNumber'
import {
    getCategoryCards,
    getForkLength,
    getTireGroupValues,
} from '../../../helpers/util'
import CustomDivider from '../../../components/CardInfo/components/CustomDivider'
import DropDown from '../../../components/DropDown/DropDown'
import useAuth from '../../../hooks/useAuth'
import CellLocation from '../../../components/Table/components/CellLocation'
import InputText from '../../../components/InputText/InputText'
import { Formik } from 'formik'
import {
    initialValues,
    validationSchema,
} from '../../../forms/NewEquipmentForm'
import clsx from 'clsx'
import useScreen from '../../../hooks/useScreen'

const fuelTypeOptionsList = [
    'Diesel',
    'Dual Fuel',
    'Electric',
    'Gasoline',
    'LPG',
    'N/A',
]

const requestTypeOptionsList = ['New', 'Used']

const Row = ({ children, className }) => {
    return (
        <div
            className={clsx(
                'w-full h-auto flex lg:flex-row flex-col items-start justify-start gap-x-8',
                className,
            )}
        >
            {children}
        </div>
    )
}

Row.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.object,
        PropTypes.element,
    ]),
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
}

const tireGroupValues = getTireGroupValues()

const forkLengthValues = getForkLength()

const cards = getCategoryCards()

const EquipmentRequestForm = ({
    initialValues: initialValuesProp,
    onSubmit,
    formRef,
    setIsValidForm,
    setIsFormDirty,
}) => {
    const { user } = useAuth()
    const { isMobile } = useScreen()

    const selectLocationOptions = user?.locationPreferences || []

    const fuelTypeOptions = fuelTypeOptionsList?.map((i, index) => ({
        value: i,
        id: index,
    }))

    const requestTypeOptions = requestTypeOptionsList?.map((i, index) => ({
        value: i,
        id: index,
    }))

    const formattedShiptos = selectLocationOptions?.map(item => {
        return {
            ...item,
            locationToShow: (
                <CellLocation
                    isInCard
                    label={item?.label}
                    address={item?.address}
                />
            ),
        }
    })

    const changeForm = () => {
        setIsValidForm(formRef?.current?.isValid)
        setIsFormDirty && setIsFormDirty(formRef?.current?.dirty)
    }

    const categories = cards.filter(card => card.category != 'allClasses')

    return (
        <Formik
            innerRef={f => (formRef.current = f)}
            id="submit-used-new-equipment"
            initialValues={initialValues(initialValuesProp)}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={formValues => {
                onSubmit && onSubmit(formValues)
            }}
        >
            {({
                getFieldProps,
                setFieldValue,
                errors,
                touched,
                values,
                handleSubmit,
                dirty,
            }) => {
                // Track dirty state changes
                useEffect(() => {
                    setIsFormDirty && setIsFormDirty(dirty)
                }, [dirty])

                return (
                    <form
                        className="w-full h-full lg:px-2 lg:py-4 px-0 py-0 flex flex-col items-center justify-start lg:gap-y-2 gap-y-1"
                        onSubmit={handleSubmit}
                        autoComplete="off"
                    >
                        <Row>
                            <DropDown
                                data={categories}
                                keyId="category"
                                keyToShow="name"
                                keyToFind="name"
                                setSelectedValue={val => {
                                    setFieldValue('categories', val?.category)
                                    setTimeout(() => {
                                        changeForm()
                                    }, 1)
                                }}
                                selected={cards?.find(
                                    i => i?.category === values?.categories,
                                )}
                                placeholder="Choose"
                                label="Select Class"
                                className="lg:rowRentalItem flex-grow w-full"
                                required
                                isSmall
                                error={
                                    touched['categories'] &&
                                    errors['categories']
                                }
                            />

                            <DropDown
                                data={requestTypeOptions}
                                keyId="value"
                                keyToShow="value"
                                keyToFind="value"
                                setSelectedValue={val =>
                                    setFieldValue('requestType', val?.value)
                                }
                                selected={
                                    requestTypeOptions?.find(
                                        i => i?.value === values?.requestType,
                                    ) || null
                                }
                                placeholder="Select Equipment Type"
                                label="Equipment Type"
                                className="w-full flex-grow"
                                required
                                isSmall
                                error={
                                    touched['requestType'] &&
                                    errors['requestType']
                                }
                            />
                        </Row>

                        {!isMobile && <CustomDivider />}
                        <Row>
                            <InputNumber
                                label="Min Capacity"
                                required
                                className="w-full flex-grow  lg:mb-0 mb-6"
                                extraLabel="lbs"
                                incrementNumber={500}
                                min={3000}
                                max={values?.maxCapacity}
                                initialValue={values?.minCapacity}
                                onChange={val =>
                                    setFieldValue('minCapacity', val)
                                }
                            />
                            <InputNumber
                                label="Max Capacity"
                                required
                                className="w-full flex-grow lg:mb-0 mb-6"
                                extraLabel="lbs"
                                incrementNumber={500}
                                min={values?.minCapacity}
                                max={55000}
                                initialValue={values?.maxCapacity}
                                onChange={val =>
                                    setFieldValue('maxCapacity', val)
                                }
                            />
                        </Row>

                        {!isMobile && <CustomDivider />}

                        <Row>
                            <DropDown
                                data={fuelTypeOptions}
                                keyId="value"
                                keyToShow="value"
                                keyToFind="value"
                                setSelectedValue={val =>
                                    setFieldValue('fuelType', val?.value)
                                }
                                selected={
                                    fuelTypeOptions?.find(
                                        i => i?.value === values?.fuelType,
                                    ) || null
                                }
                                placeholder="Select Fuel Type"
                                label="Fuel Type"
                                className="w-full flex-grow"
                                required
                                isSmall
                                error={
                                    touched['fuelType'] && errors['fuelType']
                                }
                            />

                            <DropDown
                                customRender={({ label, address }) => (
                                    <CellLocation
                                        isInCard
                                        label={label}
                                        address={address}
                                    />
                                )}
                                data={formattedShiptos}
                                keyId="id"
                                keyToShow="locationToShow"
                                keyToFind="address"
                                setSelectedValue={val => {
                                    setFieldValue(
                                        'deliverToAccountId',
                                        val?.value,
                                    )
                                    setTimeout(() => {
                                        changeForm()
                                    }, 1)
                                }}
                                selected={selectLocationOptions?.find(
                                    i =>
                                        i?.value === values?.deliverToAccountId,
                                )}
                                placeholder="Select Location"
                                label="Where do you need it?"
                                className="lg:rowRentalItem flex-grow w-full lg:mb-0 mb-2"
                                isSmall={!values?.deliverToAccountId}
                                required
                                error={
                                    touched['deliverToAccountId'] &&
                                    errors['deliverToAccountId']
                                }
                            />
                        </Row>

                        {!isMobile && <CustomDivider />}

                        <Row>
                            <DropDown
                                data={forkLengthValues}
                                keyId="key"
                                keyToShow="label"
                                keyToFind="label"
                                setSelectedValue={val =>
                                    setFieldValue('forkLength', val?.value)
                                }
                                selected={forkLengthValues?.find(
                                    i => i?.value === values?.forkLength,
                                )}
                                placeholder="Select"
                                label="Fork Length"
                                className="lg:rowRentalItem flex-grow w-full lg:mb-0 mb-2"
                                isSmall
                                error={
                                    touched['forkLength'] &&
                                    errors['forkLength']
                                }
                            />
                            <DropDown
                                data={tireGroupValues}
                                keyId="key"
                                keyToShow="label"
                                keyToFind="label"
                                setSelectedValue={val =>
                                    setFieldValue('tireGroup', val?.value)
                                }
                                selected={tireGroupValues?.find(
                                    i => i?.value === values?.tireGroup,
                                )}
                                placeholder="Select"
                                label="Tire Group"
                                className="lg:rowRentalItem flex-grow w-full lg:mb-0 mb-2"
                                isSmall
                                error={
                                    touched['tireGroup'] && errors['tireGroup']
                                }
                            />
                        </Row>

                        {!isMobile && <CustomDivider />}
                        <Row>
                            <InputText
                                {...getFieldProps('comments')}
                                placeholder="Please let us know any other details"
                                type="textarea"
                                label="Comments"
                                additionalClass="lg:rowRentalItem flex-grow w-full"
                                error={
                                    touched['comments'] && errors['comments']
                                }
                            />
                        </Row>
                    </form>
                )
            }}
        </Formik>
    )
}

EquipmentRequestForm.propTypes = {
    initialValues: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
    onSubmit: PropTypes.func,
    setError: PropTypes.func,
    formRef: PropTypes.oneOfType([PropTypes.object]),
    setIsValidForm: PropTypes.func,
    setIsFormDirty: PropTypes.func,
}

export default EquipmentRequestForm
